"use client";

import { useMemo, useState } from "react";
import type { FormEvent } from "react";

import { jobsService } from "@/services";
import type { FieldSection, JobFormField } from "@/types";

export function useApplyForm(jobId: string, fields: JobFormField[]) {
  const [openSections, setOpenSections] = useState<Set<FieldSection>>(
    () => new Set(["basicInformation"]),
  );
  const [values, setValues] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sectioned = useMemo(() => {
    const out: Record<FieldSection, JobFormField[]> = {
      basicInformation: [],
      contactDetail: [],
      education: [],
      experience: [],
    };
    for (const f of [...fields].sort((a, b) => a.sort_order - b.sort_order)) {
      const sec = (f.section_key as FieldSection) ?? "basicInformation";
      if (!out[sec]) continue;
      out[sec].push(f);
    }
    return out;
  }, [fields]);

  const setValue = (key: string, value: string) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  const toggleSection = (sec: FieldSection) =>
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(sec)) next.delete(sec);
      else next.add(sec);
      return next;
    });

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const full_name = (values["full_name"] ?? "").trim();
      const email = (values["email"] ?? "").trim();
      const phone = (values["phone"] ?? "").trim() || undefined;

      if (!full_name || !email) {
        setError("Full name and email are required.");
        setSubmitting(false);
        return;
      }

      const skip = new Set(["full_name", "email", "phone"]);
      const extras: string[] = [];
      for (const f of fields) {
        const k = f.field_key;
        if (!k || skip.has(k)) continue;
        const v = (values[k] ?? "").trim();
        if (!v) continue;
        extras.push(`${f.field_label}: ${v}`);
      }
      const cover_letter = extras.length ? extras.join("\n\n") : undefined;

      await jobsService.submitApplication({
        job_id: jobId,
        applicant_name: full_name,
        email,
        phone,
        cover_letter,
        source: "website",
      });

      setSubmitted(true);
    } catch {
      setError("Sorry — we couldn't submit your application. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return {
    sectioned,
    openSections,
    values,
    submitting,
    submitted,
    error,
    setValue,
    toggleSection,
    handleSubmit,
  };
}
